/**
 * Private-tenant {@link BlobStore} that round-trips through an authenticated
 * REST API (presign + read-proxy). This is the read/**write** path for a
 * project's *own* blobs — uploads land in private storage via a presigned PUT
 * (or a direct-PUT upload endpoint), and reads go back through a read-proxy URL.
 *
 * Contrast with the public, read-only `createRestBlobStore` (`./rest`), which
 * GETs content-addressed bytes from a public bucket for mounted/managed content.
 * This one is for content the tenant owns and mutates; that one is for content
 * published out-of-band and merely read.
 *
 * This module only uses `fetch`, so it is safe in browser bundles. It houses
 * all the upload glue that previously lived inline in the browser HAL.
 */

import { invariant } from "ts-invariant";

import type { BlobStore } from "./index";

export interface PrivateBlobStoreConfig {
  /**
   * POST endpoint that issues a presigned upload. Receives
   * `{ filename, contentType }` and must return a {@link PresignResponse}.
   * Mutually exclusive with `uploadUrl`; one of the two is required.
   */
  presignUrl?: string;
  /**
   * Builds a direct-PUT upload endpoint for a filename. When set, `create`
   * PUTs the bytes straight to `uploadUrl(filename)` (the server streams them
   * into storage) and reads back `{ filename, uri }` — no presign round-trip.
   * This is the Cloudflare/R2 path.
   */
  uploadUrl?: (filename: string) => string;
  /** Builds the read-proxy URL for a stored URI. Defaults to `/api/files?source=<uri>`. */
  readUrl?: (uri: string) => string;
  /** Builds a DELETE endpoint for a stored URI. When omitted, `remove` throws. */
  removeUrl?: (uri: string) => string;
  /** Injectable `fetch`, defaulting to the global, as in `createRestBlobStore`. */
  fetch?: typeof fetch;
}

interface PresignResponse {
  filename: string;
  uploadUrl: string;
  fileUrl: string;
  method: string;
  headers: Record<string, string>;
}

const defaultReadUrl = (uri: string) =>
  `/api/files?source=${encodeURIComponent(uri)}`;

export const createPrivateBlobStore = (
  config: PrivateBlobStoreConfig,
): BlobStore => {
  const readUrl = config.readUrl ?? defaultReadUrl;
  const fetch = config.fetch ?? globalThis.fetch.bind(globalThis);
  const fetchBlob = (uri: string) =>
    fetch(readUrl(uri), { redirect: "follow" });

  return {
    create: async ({ filename, data, contentType }) => {
      // Drain streams into a Blob first. Chrome's streaming-body fetch requires
      // HTTP/2 and rejects non-303 redirects, and S3-compatible presigned PUTs
      // reject `Transfer-Encoding: chunked`. Buffering keeps Content-Length set;
      // sandbox memory stays bounded because the child streams chunks across
      // the MessagePort.
      const rawBody = data instanceof Response ? (data.body ?? "") : data;
      const body =
        rawBody instanceof ReadableStream
          ? await new Response(rawBody).blob()
          : rawBody;

      if (config.uploadUrl) {
        // Direct PUT — the server streams the body into storage and returns
        // `{ filename, uri }`. No presign round-trip.
        const res = await fetch(config.uploadUrl(filename), {
          method: "PUT",
          headers: contentType ? { "Content-Type": contentType } : undefined,
          body: body as BodyInit,
        });
        invariant(res.ok, `Upload failed: ${res.status}`);
        const result = (await res.json()) as { filename: string; uri: string };
        return { filename: result.filename, uri: result.uri };
      }

      invariant(
        config.presignUrl,
        "Private blob store needs presignUrl or uploadUrl",
      );
      const presignRes = await fetch(config.presignUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename, contentType }),
      });
      invariant(
        presignRes.ok,
        `Failed to get upload URL: ${presignRes.status}`,
      );
      const presign = (await presignRes.json()) as PresignResponse;
      const uploadRes = await fetch(presign.uploadUrl, {
        method: presign.method,
        headers: presign.headers,
        body: body as BodyInit,
      });
      invariant(uploadRes.ok, `Upload failed: ${uploadRes.status}`);

      return { filename: presign.filename, uri: presign.fileUrl };
    },

    bytes: async (uri) => {
      const res = await fetchBlob(uri);
      invariant(res.ok, `Failed to fetch blob: ${res.status}`);
      return new Uint8Array(await res.arrayBuffer());
    },

    stream: (uri) => {
      const { readable, writable } = new TransformStream<Uint8Array>();
      fetchBlob(uri)
        .then((res) => {
          invariant(res.ok, `Failed to fetch blob: ${res.status}`);
          invariant(res.body, "Response body is null");
          res.body.pipeTo(writable);
        })
        .catch((err) => writable.abort(err));
      return readable;
    },

    size: async (uri) => {
      const res = await fetch(readUrl(uri), {
        method: "HEAD",
        redirect: "follow",
      });
      if (!res.ok) return 0;
      const len = res.headers.get("content-length");
      return len ? Number(len) : 0;
    },

    mtime: async () => new Date(0),

    write: async () => {
      throw new Error(
        "Private blob store does not support in-place writes; use create()",
      );
    },

    remove: async (uri) => {
      invariant(
        config.removeUrl,
        "Private blob store has no removeUrl configured; cannot remove blobs",
      );
      const res = await fetch(config.removeUrl(uri), { method: "DELETE" });
      invariant(res.ok, `Delete failed: ${res.status}`);
    },

    // Pinning is the server's job (it holds the bucket); a browser peer has
    // no immutable place to copy to, so a client-side capture with blobs in
    // scope fails rather than recording a mutable uri as a version.
    pin: async (uri) => {
      throw new Error(
        `Private blob store cannot pin ${uri}; capture blobs on the server`,
      );
    },
  };
};
